import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import { WidgetManager } from '../utility/widget-utils/widget-manager.js';
import { LoggerService } from '../logger/logger.service';

@Injectable({
    providedIn: 'root'
})
export class DavinciWidgetService {
    widgetManager: WidgetManager;
    private apiCommonService = inject(APICommonService);
    private http = inject(HttpClient);
    private loggerService = inject(LoggerService);
    private sessionTimeoutSubject = new Subject<void>();
    private successSubject = new Subject<void>();
    private userCancelledSubject = new Subject<void>();
    private widgetErrorSubject = new Subject<any>();

    constructor() {
        this.widgetManager = new WidgetManager();
    }

    get sessionTimeout$(): Observable<void> {
        return this.sessionTimeoutSubject.asObservable();
    }

    get success$(): Observable<void> {
        return this.successSubject.asObservable();
    }

    get userCancelled$(): Observable<void> {
        return this.userCancelledSubject.asObservable();
    }

    get widgetError$(): Observable<any> {
        return this.widgetErrorSubject.asObservable();
    }

    getDavinciToken(payload: any, endpoint: string): Observable<any> {
        const url = `${environment.APIs.apigeeEnvironment + environment.APIs.davinciToken}/${endpoint}`;
        let headers: HttpHeaders = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Accept', 'application/json');
        headers = headers.append('client_id', environment.authorize.client_id);
        headers = headers.append('X-NW-Message-ID', this.apiCommonService.generateTransactionId());
        headers = headers.set('Authorization', `Bearer ${sessionStorage.getItem('jwt')}`);
        return this.http.post<any>(url, payload, { headers }).pipe(
            catchError((err) => {
                this.loggerService.info(`getDavinciToken error`, { error: err });
                return throwError(() => err);
            })
        );
    }

    renderWidget(token: string, type: string): WidgetManager {
    const containerElement = document.getElementById(type);
    if (!containerElement) {
        this.loggerService.error('Widget container not found', { type });
        return null;
    }
    // Create/get the widget manager from the container
    this.widgetManager = containerElement as any; // or cast appropriately
    // Render the widget first
    const renderedWidget = this.widgetManager.renderWidget(token);
    // THEN attach event listeners after rendering
    this.initializeWidgetManager();
    return renderedWidget;
}

    private getWidgetManager(containerId: string): HTMLElement {
        return document.getElementById(containerId);
    }

    private initializeWidgetManager(): void {
        this.widgetManager.addEventListener('error', error => {
            // TODO: Implement launch widget error handling
            // Advice: Display a generic friendly error message.
            // error.detail = More error details
            this.loggerService.error('Davinci widget error', error);
            this.widgetErrorSubject.next(error);
        });

        this.widgetManager.addEventListener('user-cancelled', error => {
            this.loggerService.info('Davinci widget user cancelled', error);
            this.userCancelledSubject.next( error);
            // TODO: Implement user cancelled logic
            // Advice: User closed the flow, take this as a cancellation action.
            // error.detail = More error details
        });

        this.widgetManager.addEventListener('session-timeout', error => {
            // TODO: Implement widget session timeout logic
            // Advice: Call widgetManager.renderWidget('...') again with a new access token.
            // error.detail = More error details
            this.loggerService.info('Davinci widget session timed out', error);
            this.sessionTimeoutSubject.next(error);
        });

        this.widgetManager.addEventListener('success', response => {
            // TODO: Implement your success logic
            // See 'The success event' section
            // const callbackValue = response.detail?.additionalProperties?.CallBack || '';
            // response.detail = More success details
            this.loggerService.info('Davinci widget success', response);
            this.successSubject.next(response);
        });
    }
}